import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, Loader2 } from "lucide-react";
import { BookingDetailsModal } from "@/components/BookingDetailsModal";
import { trackEvent, attachCampaign } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Static product shape — avoids a network round-trip to /products before
// the modal opens. The real SKU + price is enforced server-side at
// checkout creation, so this is purely for the UI.
const KIDS_TASTER_PRODUCT = {
  sku: "kids-taster-5",
  title: "USDD Kids £5 Taster Class",
  price: 5,
  category: "kids",
};

export const KidsTasterCTA = () => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const onConfirm = async (booking) => {
    try {
      setBusy(true);
      const bookingWithUtm = attachCampaign(booking);
      trackEvent("begin_checkout", {
        sku: KIDS_TASTER_PRODUCT.sku,
        method: "stripe",
        value: KIDS_TASTER_PRODUCT.price,
        currency: "GBP",
        content_ids: [KIDS_TASTER_PRODUCT.sku],
        content_name: KIDS_TASTER_PRODUCT.title,
        content_type: "product",
      });
      trackEvent("kids_taster_book", { sku: KIDS_TASTER_PRODUCT.sku });
      const r = await axios.post(`${API}/checkout/stripe`, {
        sku: KIDS_TASTER_PRODUCT.sku,
        quantity: 1,
        origin_url: window.location.origin,
        customer_email: booking.email,
        booking: bookingWithUtm,
      });
      if (r.data?.url) {
        setOpen(false);
        window.location.href = r.data.url;
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Checkout failed — please try again.";
      toast.error(msg);
      throw new Error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="kids-taster-section" className="relative py-16 sm:py-20 bg-black overflow-hidden">
      <div className="absolute -top-24 right-1/4 w-[480px] h-[480px] rounded-full bg-brand-yellow/10 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-24 left-1/4 w-[400px] h-[400px] rounded-full bg-brand-purpleLight/15 blur-[110px] pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-6 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="relative overflow-hidden rounded-3xl border border-brand-yellow/40 bg-gradient-to-br from-brand-purpleDeep/40 via-zinc-950 to-black p-7 sm:p-10"
        >
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/70 to-transparent" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 mb-3">
                <Sparkles size={14} className="text-brand-yellow" />
                <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.32em] text-brand-yellow">
                  Paid Taster · £5
                </span>
              </div>
              <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-wide leading-[0.95]">
                USDD Kids <span className="text-brand-yellow text-glow-yellow">£5 Taster Class</span>.
              </h3>
              <p className="mt-4 max-w-xl text-sm sm:text-base text-zinc-300 leading-relaxed">
                Try a USDD Kids class before committing to the full term. A fun, confidence-building street dance session for children and young people. We'll capture the safeguarding details before checkout — then it's straight to the floor.
              </p>
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-zinc-300">
                <li className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-brand-yellow" /> Ages 6–17</li>
                <li className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-brand-yellow" /> Medway · London · outdoor</li>
                <li className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-brand-yellow" /> DBS-checked teachers</li>
                <li className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-brand-yellow" /> Just £5, fully refundable if we cancel</li>
              </ul>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <button
                type="button"
                onClick={() => setOpen(true)}
                disabled={busy}
                data-testid="kids-taster-cta-btn"
                className="inline-flex items-center justify-between gap-2 px-6 py-4 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider hover:bg-brand-yellowHover hover:scale-[1.02] transition-all glow-yellow disabled:opacity-60"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : null}
                Book £5 Taster <ArrowRight size={16} />
              </button>
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 text-center sm:text-right">
                Secure checkout · Live Stripe
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <BookingDetailsModal
        open={open}
        onClose={() => setOpen(false)}
        product={KIDS_TASTER_PRODUCT}
        onConfirm={onConfirm}
        submitLabel="Continue to Card"
      />
    </section>
  );
};
